// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React, { useEffect, useState } from "react";
import { Accordion, Button, Form, Icon, Label, Message, Modal, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ChartConfiguration, ChartDiagnostic, ClearChartConfigurationRequest } from "../../api/charts/v1/icharts";
import CopyToClipboardButton from "../../ui/_copybutton";
import { ConfirmWrite, DiagnosticsList, hasErrors } from "./utils";

interface ConfigurationTabArgs {
  config?: ChartConfiguration;
  canEdit: boolean;
  confirm: ConfirmWrite;
  onReload: () => Promise<void>;
  onError: (error: any) => void;
}

const exampleYaml = `# Applies to all charts, exported to charts.yaml.
default_values:
  replicas: 2

# Applies to all charts of this environment, never exported.
environment_values:
  images:
    application:
      registry: dev.registry.license.arango.ai

# Per chart. A chart that is not in the template needs addition: true and a version.
packages:
  mlflow:
    version: 1.2.3 # optional
    enabled: true # optional, autostart on new deployments
    overrides: # optional, Helm values
      replicas: 3
  my-test-chart:
    addition: true
    version: 0.1.0
`;

const ConfigurationHelp = ({ canEdit, empty, onInsert }: { canEdit: boolean; empty: boolean; onInsert: () => void }) => {
  const [open, setOpen] = useState(false);
  return (
    <Accordion styled fluid style={{ marginBottom: "1rem" }}>
      <Accordion.Title active={open} onClick={() => setOpen(!open)}>
        <Icon name="dropdown" />
        Format
      </Accordion.Title>
      <Accordion.Content active={open}>
        <p>
          The keys are <code>default_values</code>, <code>environment_values</code> and <code>packages</code>. Helm values of a chart go under{" "}
          <code>overrides</code>. Other keys are rejected when saving, use Validate to check the configuration first.
        </p>
        <pre style={{ fontFamily: "monospace" }}>{exampleYaml}</pre>
        {canEdit && <Button size="small" content="Insert example" onClick={onInsert} disabled={!empty} title="Only available while the editor is empty" />}
      </Accordion.Content>
    </Accordion>
  );
};

const ExportModal = ({ yaml, onClose }: { yaml: string; onClose: () => void }) => {
  const download = () => {
    const url = URL.createObjectURL(new Blob([yaml], { type: "text/yaml" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "charts.yaml";
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header>charts.yaml</Modal.Header>
      <Modal.Content scrolling>
        <p>The template with the chart configuration and the default values baked in. The environment values are not included.</p>
        <pre style={{ fontFamily: "monospace" }}>{yaml}</pre>
      </Modal.Content>
      <Modal.Actions>
        <CopyToClipboardButton content={yaml} />
        <Button icon="download" content="Download" onClick={download} />
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

const ConfigurationTab = ({ config, canEdit, confirm, onReload, onError }: ConfigurationTabArgs) => {
  const [yaml, setYaml] = useState((config && config.yaml) || "");
  const [exported, setExported] = useState<string>();
  const [diagnostics, setDiagnostics] = useState<ChartDiagnostic[]>();

  useEffect(() => {
    setYaml((config && config.yaml) || "");
    setDiagnostics(undefined);
  }, [config && config.yaml]);

  const environment = (config && config.environment) || "";
  const dirty = yaml !== ((config && config.yaml) || "");
  const entries = (config && config.charts) || [];
  const redundantDefaults = (config && config.redundant_default_value_paths) || [];

  const handleSave = () =>
    confirm({ header: `Save the chart configuration of ${environment}?` }, async () => {
      try {
        await apiClients.idashboardClient.UpdateChartConfiguration({ yaml });
        await onReload();
      } catch (error) {
        onError(error);
      }
    });

  const handleValidate = async () => {
    try {
      const response = await apiClients.idashboardClient.ValidateChartConfiguration({ yaml });
      setDiagnostics(response.diagnostics || []);
    } catch (error) {
      onError(error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiClients.idashboardClient.ExportChartsYAML({});
      setExported(response.yaml || "");
    } catch (error) {
      onError(error);
    }
  };

  const handleClear = (header: string, content: string, req: ClearChartConfigurationRequest, typed?: string) =>
    confirm({ header, content, confirm: typed, always: true }, async () => {
      try {
        await apiClients.idashboardClient.ClearChartConfiguration(req);
        await onReload();
      } catch (error) {
        onError(error);
      }
    });

  return (
    <>
      {exported !== undefined && <ExportModal yaml={exported} onClose={() => setExported(undefined)} />}
      <p>
        The chart configuration of this environment, as YAML. To promote a tested change, export charts.yaml, commit it to the template in config, and clear the
        redundant entries once it is released.
      </p>
      <div style={{ marginBottom: "1rem" }}>
        <Button size="small" icon="file code outline" content="Export charts.yaml" onClick={handleExport} />
        {canEdit && (
          <>
            <Button
              size="small"
              content="Clear redundant"
              onClick={() => handleClear("Clear redundant configuration?", "Removes the configured fields that equal the template.", { redundant_only: true })}
            />
            <Button
              size="small"
              content="Clear orphaned"
              onClick={() =>
                handleClear("Clear orphaned configuration?", "Removes the entries of charts that are not in the template.", { orphaned_only: true })
              }
            />
            <Button
              size="small"
              negative
              basic
              content="Clear all"
              onClick={() => handleClear(`Clear the whole chart configuration of ${environment}?`, "Everything goes back to the template.", {}, "clear")}
            />
          </>
        )}
      </div>
      <ConfigurationHelp canEdit={canEdit} empty={!yaml.trim() || yaml.trim() === "packages: {}"} onInsert={() => setYaml(exampleYaml)} />
      <Form>
        <Form.Field>
          <textarea
            rows={24}
            spellCheck={false}
            style={{ fontFamily: "monospace" }}
            value={yaml}
            readOnly={!canEdit}
            onChange={(e) => {
              setYaml(e.target.value);
              setDiagnostics(undefined);
            }}
            placeholder={"# No chart configuration, the default charts are the template. See Format above."}
          />
        </Form.Field>
        {canEdit && (
          <>
            <Button primary icon="check" content="Save" onClick={handleSave} disabled={!dirty || !yaml.trim() || hasErrors(diagnostics)} />
            <Button content="Validate" onClick={handleValidate} disabled={!yaml.trim()} title="Check the configuration without saving it" />
            <Button content="Reset" onClick={() => setYaml((config && config.yaml) || "")} disabled={!dirty} />
          </>
        )}
      </Form>
      {diagnostics && diagnostics.length === 0 && <Message positive size="small" content="No problems found." />}
      <DiagnosticsList diagnostics={diagnostics} />
      {(entries.length > 0 || redundantDefaults.length > 0) && (
        <>
          <h4>Entries</h4>
          <Table compact size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Chart</Table.HeaderCell>
                <Table.HeaderCell>Notes</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {redundantDefaults.length > 0 && (
                <Table.Row>
                  <Table.Cell>default_values</Table.Cell>
                  <Table.Cell>
                    <Label size="tiny" basic color="orange">
                      {redundantDefaults.length} same as template
                    </Label>
                  </Table.Cell>
                </Table.Row>
              )}
              {entries.map((entry) => (
                <Table.Row key={entry.name}>
                  <Table.Cell>{entry.name}</Table.Cell>
                  <Table.Cell>
                    {entry.addition && (
                      <Label size="tiny" color="blue">
                        added
                      </Label>
                    )}
                    {entry.orphaned && (
                      <Label size="tiny" color="red">
                        orphaned
                      </Label>
                    )}
                    {entry.version_redundant && (
                      <Label size="tiny" basic color="orange">
                        version same as template
                      </Label>
                    )}
                    {entry.enabled_redundant && (
                      <Label size="tiny" basic color="orange">
                        enabled same as template
                      </Label>
                    )}
                    {(entry.redundant_value_paths || []).length > 0 && (
                      <Label size="tiny" basic color="orange">
                        {(entry.redundant_value_paths || []).length} values same as template
                      </Label>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </>
  );
};

export default ConfigurationTab;
